import { useEffect, useRef, useState, type CSSProperties, type PointerEvent, type ReactNode } from 'react';
import { useDialogFocus } from '@/hooks/useDialogFocus';
import { useNoticeStackClearance, useNoticeStackKeyboardRoom } from '@/hooks/useNoticeStackReserve';
import styles from './UpdateSheet.module.css';

const DISMISS_FRACTION = 0.3;
const VELOCITY_PX_MS = 0.55;

interface SheetShellProps {
  ariaLabel: string;
  onBackdrop: () => void;
  closeLabel: string;
  children: ReactNode;
  /**
   * Coordinator-computed z for this sheet's layer (A1, §3.7 L3). Applied to
   * BOTH the backdrop and the sheet so the pair sits at the layer's stack
   * position together; the sheet still paints above its own backdrop via DOM
   * source order at equal z. When undefined, the CSS fallback (backdrop 200 /
   * sheet 201) applies — so non-migrated callers (LeafActionSheet, A2) are
   * unaffected.
   */
  zIndex?: number;
  /**
   * Opt-in drag-to-dismiss (Scripture Journal UX arc Chunk 4). Pointer follows
   * the grab-handle zone and any `[data-sheet-drag]` header. Dismisses past
   * ~30% of sheet height or a downward flick, then calls `onBackdrop` so the
   * NavStack layer unwinds. Disabled under prefers-reduced-motion (tap-away
   * and Escape still close).
   */
  dragToDismiss?: boolean;
}

/**
 * Slide-up sheet shell shared by every bottom-sheet surface in the app.
 * Owns the backdrop, the slide-up animation, the drag handle, and the
 * iOS keyboard-inset tracking that lifts the sheet above the on-screen
 * keyboard. Caller owns everything inside `children` (per-variant
 * header, prayer context, composer body).
 *
 * Lifted out of UpdateSheet.tsx in the Sub-chunk A refactor so future
 * sheet consumers (the Sacred Leaf redesign in PrayerDetail) can reuse
 * the same shell primitive without duplicating the visualViewport
 * keyboard math.
 */
export default function SheetShell({
  ariaLabel,
  onBackdrop,
  closeLabel,
  children,
  zIndex,
  dragToDismiss = false,
}: SheetShellProps) {
  // Track the visual-viewport keyboard inset and apply it as `bottom` so the
  // sheet always sits ABOVE the iOS keyboard. CSS `bottom: 0` + `max-height:
  // 85dvh` alone isn't reliable on iOS Safari: position-fixed elements pin
  // to the LAYOUT viewport (which keeps full height), so the sheet's
  // bottom edge can render behind the keyboard until iOS recalculates.
  // The "tap +pray, sheet hidden, scroll to find it, second time it works"
  // bug was exactly this — first tap loses the race between sheetSlideUp
  // and the iOS keyboard animation, second tap wins it.
  //
  // visualViewport is supported on Chrome/Edge/Firefox/Safari (and every
  // iOS browser is forced to WebKit so the same fix applies). On desktop
  // browsers there's no on-screen keyboard so the diff stays at 0 and
  // `bottom: 0px` is identical to the previous behavior. The 100px
  // threshold filters out small diffs from browser-toolbar show/hide.
  const [keyboardInset, setKeyboardInset] = useState(0);
  const [dragY, setDragY] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [snapping, setSnapping] = useState(false);
  const sheetRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    pointerId: number;
    startY: number;
    lastY: number;
    lastT: number;
    velocity: number;
  } | null>(null);
  const dragYRef = useRef(0);
  const snapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // App notice stack (v0.2.0 finishing): the stack is fixed above every sheet
  // that paints in an isolated page context (PrayPage, the Bible reader), so
  // while a notice shows the sheet's top stays at or below the stack's
  // clearance (0 otherwise, and the sheet is unchanged).
  const noticeClearance = useNoticeStackClearance();
  // Keyboard room (v0.2.0 follow-through): while the keyboard is open AND a
  // notice shows, the sheet asks the stack for the room its focused field and
  // its action row need above the keyboard, keeps the field in view and marks
  // itself `data-keyboard-room` (the consumer's `[data-keyboard-actions]` row
  // is then sticky at the sheet's bottom). Nothing changes otherwise.
  const keyboardRoom = keyboardInset > 0 && noticeClearance > 0;
  useNoticeStackKeyboardRoom(keyboardRoom, sheetRef);

  const reduceMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const canDrag = dragToDismiss && !reduceMotion;

  useDialogFocus({
    open: true,
    containerRef: sheetRef,
    onEscape: onBackdrop,
  });

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      const diff = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      setKeyboardInset(diff > 100 ? diff : 0);
    };
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    update();
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (snapTimerRef.current) clearTimeout(snapTimerRef.current);
    };
  }, []);

  // iOS Safari (standalone PWA especially) ignores `touch-action: none` for
  // the DOCUMENT rubber-band gesture: the pointer drag moves the sheet, but
  // WebKit simultaneously pulls the whole viewport down and can leave it
  // stuck offset until the user taps the status bar. A non-passive touchmove
  // preventDefault while a drag is active is the one signal WebKit always
  // honors. dragRef is set synchronously in onPointerDown (which fires
  // before the first touchmove), so there is no attach race.
  useEffect(() => {
    if (!canDrag) return;
    const blockNativeScroll = (e: TouchEvent) => {
      if (dragRef.current && e.cancelable) e.preventDefault();
    };
    window.addEventListener('touchmove', blockNativeScroll, { passive: false });
    return () => window.removeEventListener('touchmove', blockNativeScroll);
  }, [canDrag]);

  const isDragStartTarget = (target: EventTarget | null) => {
    if (!(target instanceof Element)) return false;
    return Boolean(
      target.closest(`.${styles.dragZone}`) || target.closest('[data-sheet-drag]'),
    );
  };

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (!canDrag || e.button !== 0) return;
    if (!isDragStartTarget(e.target)) return;
    e.preventDefault();
    const now = performance.now();
    dragRef.current = {
      pointerId: e.pointerId,
      startY: e.clientY,
      lastY: e.clientY,
      lastT: now,
      velocity: 0,
    };
    dragYRef.current = 0;
    setSnapping(false);
    setDragging(true);
    setDragY(0);
    sheetRef.current?.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    const now = performance.now();
    const dt = now - d.lastT;
    if (dt > 0) d.velocity = (e.clientY - d.lastY) / dt;
    d.lastY = e.clientY;
    d.lastT = now;
    const dy = Math.max(0, e.clientY - d.startY);
    dragYRef.current = dy;
    setDragY(dy);
  };

  const endDrag = (e: PointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    dragRef.current = null;
    setDragging(false);
    const height = sheetRef.current?.offsetHeight ?? 0;
    const dy = dragYRef.current;
    const shouldDismiss =
      dy > height * DISMISS_FRACTION || d.velocity > VELOCITY_PX_MS;
    if (shouldDismiss) {
      setDragY(0);
      dragYRef.current = 0;
      onBackdrop();
      return;
    }
    setSnapping(true);
    setDragY(0);
    dragYRef.current = 0;
    if (snapTimerRef.current) clearTimeout(snapTimerRef.current);
    snapTimerRef.current = setTimeout(() => setSnapping(false), 250);
  };

  const sheetStyle: CSSProperties = {};
  if (zIndex !== undefined) sheetStyle.zIndex = zIndex;
  if (keyboardInset > 0) {
    sheetStyle.bottom = `${keyboardInset}px`;
    // Cap height to the visible area above the keyboard. Top buffer
    // uses max(env(safe-area-inset-top), 16px) so the sheet clears
    // the Dynamic Island (62px on iPhone 16/17 Pro, 59px on 14-16
    // non-Pro, 47-50px on notched pre-Dynamic-Island phones). With a
    // plain 16px literal the sheet's top edge rendered at viewport
    // y=16, putting the drag handle + header inside the Island pill.
    // While a notice shows, the stack's clearance (always below the safe area
    // and the 16px buffer, since the stack sits below both) is the top buffer
    // instead, so the sheet's top stays clear of the notices too.
    sheetStyle.maxHeight = noticeClearance > 0
      ? `calc(100dvh - ${keyboardInset}px - ${noticeClearance}px)`
      : `calc(100dvh - ${keyboardInset}px - max(env(safe-area-inset-top, 0px), 16px))`;
  } else if (noticeClearance > 0) {
    sheetStyle.maxHeight = `min(90dvh, calc(100dvh - ${noticeClearance}px))`;
  }
  if (canDrag && (dragging || snapping || dragY > 0)) {
    sheetStyle.transform = `translateY(${dragY}px)`;
  }
  const hasSheetStyle = Object.keys(sheetStyle).length > 0;

  const sheetClass = [
    styles.sheet,
    dragging ? styles.sheetDragging : '',
    snapping ? styles.sheetDragSnap : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <button
        type="button"
        className={styles.backdrop}
        onClick={onBackdrop}
        aria-label={closeLabel}
        style={zIndex !== undefined ? { zIndex } : undefined}
      />
      <div
        ref={sheetRef}
        className={sheetClass}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        tabIndex={-1}
        style={hasSheetStyle ? sheetStyle : undefined}
        // Capped below the notices, the sheet scrolls as one column (see
        // `.sheet[data-notice-capped]`), so every control stays reachable.
        data-notice-capped={noticeClearance > 0 ? '' : undefined}
        data-keyboard-room={keyboardRoom ? '' : undefined}
        onPointerDown={canDrag ? onPointerDown : undefined}
        onPointerMove={canDrag ? onPointerMove : undefined}
        onPointerUp={canDrag ? endDrag : undefined}
        onPointerCancel={canDrag ? endDrag : undefined}
      >
        {canDrag ? (
          <div className={styles.dragZone}>
            <div className={styles.handle} aria-hidden="true" />
          </div>
        ) : (
          <div className={styles.handle} aria-hidden="true" />
        )}
        {children}
      </div>
    </>
  );
}
